import { useCallback, useContext, useEffect, useState } from 'react';
import EntityDetailPopup from '../../../utility/EntityDetailPopup';
import { DownArrowIcon, EyeIcon, cropClasses, miscClasses, popupClasses } from '../../../utility';
import { IconButton, InputAdornment, MenuItem, TextField } from '@mui/material';
import CropRunFRTLPopup from '../../../utility/popups/CropRunFRTLPopup';
import { getFrtlAliasString } from '../../../../utility/frtlUtils';
import { FRTLContext } from '../../../../context/FRTLContext';

export default function CreateSubPackagePopup(props) {
  const {
    open,
    onClose,
    currDarkThemeClass,
    parentValues,
    handleAddSubPackage,
    isEditMode,
    currentSubPackage,
    sourcePackageData,
    bucketInstanceData = {},
  } = props;

  const [values, setValues] = useState({
    metrc_sub_package_tag_number: '',
    frtl: '',
    metrc_unit_of_measurement: '',
    patient_license_number: '',
    sub_package_note: '',
    sub_package_date: '',
    sub_package_type: 'sub_package',
    original_sub_package_weight: '',
    current_sub_package_weight: '',
    current_sub_package_status: 'active',
    is_finished: false,
    archived_date: null,
    item: '',
    source_packages: [],
    selectedItem: '',
    frtlOpen: false,
  });

  const [metrcError, setMetrcError] = useState(false);
  const { frtlContextValue } = useContext(FRTLContext);

  useEffect(() => {
    if (isEditMode && currentSubPackage) {
      const selectedItem = bucketInstanceData?.items?.find(item => item.id === currentSubPackage.item) ?? {};

      setValues({
        ...currentSubPackage,
        sub_package_date: currentSubPackage.sub_package_date || new Date().toISOString().split('T')[0],
        archived_date: currentSubPackage.archived_date || '',
        selectedItem: selectedItem?.id || '',
        source_packages: currentSubPackage.source_packages || [],
        frtlOpen: false,
      });
    } else {
      setValues({
        metrc_sub_package_tag_number: '',
        frtl: sourcePackageData?.frtl || '',
        metrc_unit_of_measurement: sourcePackageData?.metrc_unit_of_measurement || '',
        patient_license_number: '',
        sub_package_note: '',
        sub_package_date: new Date().toISOString().split('T')[0],
        sub_package_type: 'sub_package',
        original_sub_package_weight: '',
        current_sub_package_weight: '',
        current_sub_package_status: 'active',
        is_finished: false,
        archived_date: null,
        item: sourcePackageData?.item || '',
        source_packages: sourcePackageData?.id ? [sourcePackageData.id] : [],
        selectedItem: sourcePackageData?.item || '',
        frtlOpen: false,
      });
    }
  }, [isEditMode, currentSubPackage, sourcePackageData, bucketInstanceData]);

  const handleSetValues = useCallback((prop, value) => setValues(state => ({ ...state, [prop]: value })), []);

  const handleMetrcChange = value => {
    if (value.length === 24) {
      setMetrcError(false);
    } else {
      setMetrcError(true);
    }
    handleSetValues('metrc_sub_package_tag_number', value);
  };

  const handleItemChange = itemId => {
    const selectedItem = bucketInstanceData?.items?.find(item => item.id === itemId) ?? {};
    handleSetValues('selectedItem', itemId);
    handleSetValues('item', itemId);
    handleSetValues('metrc_unit_of_measurement', selectedItem?.metrc_unit_of_measurement || '');
  };

  const handleSaveSubPackage = () => {
    const payload = {
      ...values,
    };
    delete payload.selectedItem;
    delete payload.frtlOpen;

    handleAddSubPackage(payload);
  };

  const eDPopupProps = {
    open,
    onClose,
    currDarkThemeClass,
    title: isEditMode
      ? `Edit Subpackage - ${values?.metrc_sub_package_tag_number}`
      : `Create Subpackage from ${sourcePackageData?.metrc_package_tag_number}`,
    headContentText: isEditMode ? 'Edit the subpackage details.' : 'Create a new subpackage from the selected source package.',
    onSaveClick: handleSaveSubPackage,
  };

  const cropPlanMenuItemProps = {
    className: `${cropClasses?.menuItem} ${currDarkThemeClass}`,
    focusVisibleClassName: miscClasses?.menuItemFocusDark,
  };

  const textFieldProp = ({ prop, inputProps = {}, otherTextProps = {} }) => ({
    autoComplete: 'off',
    className: `${popupClasses?.strainEntry?.textField} ${currDarkThemeClass}`,
    fullWidth: true,
    inputProps,
    label: '',
    onChange: ({ target: { value } }) => handleSetValues(prop, value),
    value: values?.[prop] ?? '',
    ...otherTextProps,
  });

  const itemsList =
    bucketInstanceData?.items?.map((item, i) => (
      <MenuItem key={i} value={item?.id} {...cropPlanMenuItemProps}>
        {item?.item_name}
      </MenuItem>
    )) ?? [];

  if (!itemsList?.length) {
    itemsList?.push(
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        No Available Items
      </MenuItem>
    );
  }

  const unitsList =
    bucketInstanceData?.unit?.map((unit, i) => (
      <MenuItem key={i} value={unit?.id} {...cropPlanMenuItemProps}>
        {unit?.unit_name}
      </MenuItem>
    )) ?? [];

  if (!unitsList?.length) {
    unitsList?.push(
      <MenuItem key={-1} value={-1} disabled {...cropPlanMenuItemProps}>
        No Available Units
      </MenuItem>
    );
  }

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
  ];

  const statusList = statusOptions.map((status, i) => (
    <MenuItem key={i} value={status.value} {...cropPlanMenuItemProps}>
      {status.label}
    </MenuItem>
  ));

  const subPackageTypeOptions = [
    { value: 'test_sample_collection', label: 'Test Sample Collection' },
    // { value: 'split', label: 'Split' },
    { value: 'subpackage', label: 'Sub Package' },
  ];

  const typeList = subPackageTypeOptions.map((type, i) => (
    <MenuItem key={i} value={type.value} {...cropPlanMenuItemProps}>
      {type.label}
    </MenuItem>
  ));

  const cppItemProps = {
    prop: 'selectedItem',
    otherTextProps: {
      disabled: isEditMode ,
      label: 'Item',
      required: true,
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
      onChange: ({ target: { value } }) => handleItemChange(value),
    },
  };

  const cppUnitProps = {
    prop: 'metrc_unit_of_measurement',
    otherTextProps: {
      label: 'Unit of Measurement',
      required: true,
      select: true,
      disabled: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
    },
  };

  const cppMetrcTagProps = {
    prop: 'metrc_sub_package_tag_number',
    otherTextProps: {
      label: 'Metrc Sub Package Tag Number',
      required: true,
      disabled: isEditMode,
      error: metrcError,
      helperText: metrcError ? 'Metrc Sub Package Tag Number must have exactly 24 characters' : null,
    },
  };

  const cppPatientLicenseProps = {
    prop: 'patient_license_number',
    otherTextProps: {
      label: 'Patient License Number',
      required: true,
    },
  };

  const cppSubPackageNoteProps = {
    prop: 'sub_package_note',
    otherTextProps: {
      label: 'Sub Package Note',
      multiline: true,
      rows: 3,
    },
  };

  const cppSubPackageDateProps = {
    prop: 'sub_package_date',
    otherTextProps: {
      // dis
      label: 'Sub Package Date',
      type: 'date',
      required: true,
      InputLabelProps: {
        shrink: true,
      },
    },
  };

  const cppSubPackageTypeProps = {
    prop: 'sub_package_type',
    otherTextProps: {
      disabled: isEditMode,
      label: 'Sub Package Type',
      required: true,
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
    },
  };

  const cppOriginalWeightProps = {
    prop: 'original_sub_package_weight',
    otherTextProps: {
      disabled: isEditMode,
      label: 'Original Sub Package Weight (grams)',
      type: 'number',
      required: true,
    },
  };

  const cppCurrentWeightProps = {
    prop: 'current_sub_package_weight',
    otherTextProps: {
      label: 'Current Sub Package Weight (grams)',
      type: 'number',
      required: true,
    },
  };

  const cppStatusProps = {
    prop: 'current_sub_package_status',
    otherTextProps: {
      label: 'Sub Package Status',
      required: true,
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
    },
  };

  const cppIsFinishedProps = {
    prop: 'is_finished',
    otherTextProps: {
      label: 'Is Finished',
      select: true,
      SelectProps: {
        IconComponent: DownArrowIcon,
      },
      onChange: ({ target: { value } }) => handleSetValues('is_finished', value === 'true'),
    },
  };

  const cppArchivedDateProps = {
    prop: 'archived_date',
    otherTextProps: {
      label: 'Archived Date',
      type: 'date',
      InputLabelProps: {
        shrink: true,
      },
      helperText: 'Leave empty if not archived',
    },
  };

  const handleOpenFRTLPopup = () => {
    handleSetValues('frtlOpen', true);
  };

  const handleFRTLClose = () => {
    handleSetValues('frtlOpen', false);
  };

  const cppFrtlProps = {
    otherTextProps: {
      label: 'FRTL (Farm - Room - Table - Level)',
      value: getFrtlAliasString(values?.frtl, frtlContextValue) ?? '',
      placeholder: 'FARM - ROOM - TABLE - LEVEL',
      required: true,
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={handleOpenFRTLPopup}>
              <EyeIcon />
            </IconButton>
          </InputAdornment>
        ),
      },
      disabled: isEditMode,
    },
    inputProps: {
      disabled: true,
    },
  };

  const cppSourcePackageProps = {
    otherTextProps: {
      label: 'Source Package',
      value: sourcePackageData?.metrc_package_tag_number || 'N/A',
      disabled: true,
      helperText: 'This subpackage will be created from the selected source package',
    },
    inputProps: {
      disabled: true,
    },
  };

  const cropRunFRTLProps = {
    open: values?.frtlOpen,
    currDarkThemeClass,
    onClose: handleFRTLClose,
    handleEditFrtl: value => handleSetValues('frtl', value),
    selectedFarmId: parentValues?.farm,
    metrcStatus: isEditMode ? 'metrc_validated' : null,
    farm: +values?.frtl?.split('.')[0] ?? '',
    room: +values?.frtl?.split('.')[1] ?? '',
    table: +values?.frtl?.split('.')[2] ?? '',
    level: +values?.frtl?.split('.')[3] ?? '',
  };

  return (
    <EntityDetailPopup {...eDPopupProps}>
      <TextField {...textFieldProp(cppSourcePackageProps)} />
      {!isEditMode && <TextField {...textFieldProp(cppMetrcTagProps)} onChange={e => handleMetrcChange(e.target.value)} />}
      <TextField {...textFieldProp(cppFrtlProps)} />
      <TextField {...textFieldProp(cppItemProps)}>{itemsList}</TextField>
      <TextField {...textFieldProp(cppUnitProps)}>{unitsList}</TextField>
      <TextField {...textFieldProp(cppPatientLicenseProps)} />
      {/* <TextField {...textFieldProp(cppSubPackageDateProps)} /> */}
      <TextField {...textFieldProp(cppSubPackageTypeProps)}>{typeList}</TextField>
      <TextField {...textFieldProp(cppOriginalWeightProps)} />
      <TextField {...textFieldProp(cppCurrentWeightProps)} />
      <TextField {...textFieldProp(cppStatusProps)}>{statusList}</TextField>
      <TextField {...textFieldProp(cppIsFinishedProps)}>
        <MenuItem value={false} {...cropPlanMenuItemProps}>No</MenuItem>
        <MenuItem value={true} {...cropPlanMenuItemProps}>Yes</MenuItem>
      </TextField>
      {/* <TextField {...textFieldProp(cppArchivedDateProps)} /> */}
      <TextField {...textFieldProp(cppSubPackageNoteProps)} />

      {values?.frtlOpen && <CropRunFRTLPopup {...cropRunFRTLProps} />}
    </EntityDetailPopup>
  );
}